import React, { useEffect, useState } from "react";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { shortDate } from "@/lib/format";

/**
 * The citizen's own event RSVPs and job applications, on their activity page. Same shape as
 * `MyAttendance`: self-hiding (nobody who has done neither sees a section) and a failed fetch hides
 * the list rather than erroring the whole page.
 */
export function MyCommunity() {
  const { t, lang } = useApp();
  const [rsvps, setRsvps] = useState([]);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    let alive = true;
    api
      .get("/event-rsvps/mine")
      .then((r) => alive && setRsvps(rows(r.data)))
      .catch(() => {});
    api
      .get("/job-applications/mine")
      .then((r) => alive && setApplications(rows(r.data)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!rsvps.length && !applications.length) return null;

  return (
    <>
      {rsvps.length > 0 && (
        <section className="mt-8 space-y-3" data-testid="my-rsvps">
          <h2 className="font-heading text-xl font-bold">{t("community.myRsvps")}</h2>
          <ul className="space-y-2">
            {rsvps.map((r) => (
              <li key={r.id} className="rounded-2xl border border-border p-4" data-testid={`my-rsvp-${r.id}`}>
                <p className="font-semibold">{localized(lang, r, "event_title")}</p>
                <p className="text-sm text-muted-foreground">
                  {[r.business_name, shortDate(r.starts_on, lang)].filter(Boolean).join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
      {applications.length > 0 && (
        <section className="mt-8 space-y-3" data-testid="my-applications">
          <h2 className="font-heading text-xl font-bold">{t("community.myApplications")}</h2>
          <ul className="space-y-2">
            {applications.map((a) => (
              <li key={a.id} className="rounded-2xl border border-border p-4" data-testid={`my-application-${a.id}`}>
                <p className="font-semibold">{localized(lang, a, "job_title")}</p>
                <p className="text-sm text-muted-foreground">{a.employer_name}</p>
                {a.note && <p className="mt-1 text-sm">{a.note}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
